/**
 * TEC-717 Quick apply: pure helpers for the SRP badge, the "Quick apply only" filter and the job
 * page's "Applications go through {vendor}" line. Kept dependency-light (no React, no Typesense
 * client) so the server-side SRP data loader, the browser search context and the components can
 * all share it.
 */
import {
  LAUNCHED_AUTOFILL_VENDORS,
  SUPPORTED_AUTOFILL_VENDORS,
  isAutofillAvailableForVendor,
} from '../apply-autofill/autofill-vendors';

/** URL query key / refinementList key for the "Quick apply only" toggle (`?quickApply=1`). */
export const QUICK_APPLY_FILTER_KEY = 'quickApply';

const ATS_VENDOR_DISPLAY_NAMES: Record<string, string> = {
  teamtailor: 'Teamtailor',
  greenhouse: 'Greenhouse',
  smartrecruiters: 'SmartRecruiters',
  workday: 'Workday',
  successfactors: 'SuccessFactors',
  oraclecloud: 'Oracle Cloud',
  recruitee: 'Recruitee',
  workable: 'Workable',
  lever: 'Lever',
  ashby: 'Ashby',
  personio: 'Personio',
  jobylon: 'Jobylon',
  talentadore: 'Talentadore',
  talentech: 'Talentech',
  comeet: 'Comeet',
  bamboohr: 'BambooHR',
  breezy: 'Breezy',
  pinpoint: 'Pinpoint',
  rippling: 'Rippling',
};

/** Human-readable ATS name for a canonical vendor key, or null when there is no vendor. */
export function atsVendorDisplayName(vendor: string | null | undefined): string | null {
  const key = typeof vendor === 'string' ? vendor.trim().toLowerCase() : '';
  if (!key) return null;
  const known = ATS_VENDOR_DISPLAY_NAMES[key];
  if (known) return known;
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** Vendors the "Quick apply only" filter may match for this user: launched, or all supported with the dev cookie. */
export function getQuickApplyVendors(hasDevCookie: boolean): string[] {
  return hasDevCookie
    ? [...SUPPORTED_AUTOFILL_VENDORS]
    : SUPPORTED_AUTOFILL_VENDORS.filter((v) => LAUNCHED_AUTOFILL_VENDORS.has(v));
}

/** True when the Quick apply badge should show for a job's vendor. */
export function shouldShowQuickApplyBadge(vendor: string | null | undefined, hasDevCookie: boolean): boolean {
  return isAutofillAvailableForVendor(vendor, hasDevCookie);
}

/** True when the "Quick apply only" toggle should be rendered at all. */
export function isQuickApplyFilterAvailable(hasDevCookie: boolean): boolean {
  return getQuickApplyVendors(hasDevCookie).length > 0;
}

/**
 * The Algolia-style OR facet-filter group (`['atsVendor:teamtailor', ...]`) that the toggle maps to,
 * or null when the filter is unavailable for this user. A null result means "ignore the filter":
 * a shared `?quickApply=1` link must not zero out results for someone it cannot apply to.
 * Typesense turns the group into an OR over `atsVendor` via build-typesense-search-params.ts.
 */
export function buildQuickApplyFacetFilter(hasDevCookie: boolean): string[] | null {
  const vendors = getQuickApplyVendors(hasDevCookie);
  if (vendors.length === 0) return null;
  return vendors.map((v) => `atsVendor:${v}`);
}

/** True when a refinementList entry carries an active quickApply toggle. */
export function isQuickApplyRefinementActive(values: unknown): boolean {
  const list = Array.isArray(values) ? values : [values];
  return list.some((v) => v === '1' || v === 'true' || v === true);
}

/**
 * The facet-filter group for a `quickApply` refinement value, or null when the toggle is off or
 * unavailable to this user (callers then add nothing). Shared by the SSR and browser filter builders.
 */
export function resolveQuickApplyFacetFilter(rawValue: unknown, hasDevCookie: boolean): string[] | null {
  if (!isQuickApplyRefinementActive(rawValue)) return null;
  return buildQuickApplyFacetFilter(hasDevCookie);
}
